import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import MessageModal from '../components/common/MessageModal';

/**
 * Feedback transversal a todas las vistas.
 *
 * Uso:
 *   const feedback = useFeedback();
 *   feedback.success('Intervalos de fechas guardados exitosamente');
 *   feedback.error('No se pudo guardar: ' + e.message);
 *   feedback.notify('Texto', { variant: 'info', title: 'Opcional' });
 *
 * Muestra un único MessageModal a la vez; si se disparan varios mensajes
 * seguidos se encolan y se muestran uno tras otro al pulsar "Aceptar".
 */
const FeedbackContext = createContext(null);

let seq = 0;

export const FeedbackProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  // `closing` = el modal está haciendo su animación de salida. Mientras dure,
  // el ítem sigue en `queue[0]` para que el modal no cambie de apariencia
  // (variante/título/texto) durante el fade out; se saca de la cola en onExited.
  const [closing, setClosing] = useState(false);
  const advancingRef = useRef(false);
  const exitTimerRef = useRef(null);

  const notify = useCallback((message, opts = {}) => {
    if (message == null || message === '') return;
    const text = typeof message === 'string' ? message : String(message);
    setQueue(q => [
      ...q,
      {
        id: ++seq,
        message: text,
        variant: opts.variant || 'info',
        title: opts.title || null,
        acceptLabel: opts.acceptLabel || 'Aceptar',
        onAccept: typeof opts.onAccept === 'function' ? opts.onAccept : null,
      },
    ]);
  }, []);

  const success = useCallback((m, opts = {}) => notify(m, { ...opts, variant: 'success' }), [notify]);
  const error   = useCallback((m, opts = {}) => notify(m, { ...opts, variant: 'error' }), [notify]);
  const warning = useCallback((m, opts = {}) => notify(m, { ...opts, variant: 'warning' }), [notify]);
  const info    = useCallback((m, opts = {}) => notify(m, { ...opts, variant: 'info' }), [notify]);

  // Saca el ítem actual de la cola y ejecuta su callback. Idempotente: lo llaman
  // tanto onExited como un temporizador de respaldo (por si la animación no
  // dispara onExited), pero solo el primero surte efecto.
  const advance = useCallback(() => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    if (exitTimerRef.current) { clearTimeout(exitTimerRef.current); exitTimerRef.current = null; }
    setClosing(false);
    setQueue(q => {
      const [done, ...rest] = q;
      if (done?.onAccept) {
        try { done.onAccept(); } catch { /* no-op */ }
      }
      return rest;
    });
  }, []);

  // "Aceptar" / cerrar: dispara la animación de salida y arma el respaldo.
  const handleClose = useCallback(() => {
    advancingRef.current = false;
    setClosing(true);
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    exitTimerRef.current = setTimeout(advance, 450);
  }, [advance]);

  const current = queue[0] || null;
  const visible = !!current && !closing;

  const value = useMemo(
    () => ({ notify, success, error, warning, info }),
    [notify, success, error, warning, info]
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <MessageModal
        show={visible}
        variant={current?.variant || 'info'}
        title={current?.title || undefined}
        message={current?.message || ''}
        acceptLabel={current?.acceptLabel || 'Aceptar'}
        onClose={handleClose}
        onExited={advance}
      />
    </FeedbackContext.Provider>
  );
};

export const useFeedback = () => {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    throw new Error('useFeedback debe usarse dentro de <FeedbackProvider>');
  }
  return ctx;
};

export default FeedbackContext;
